import { Box, Grid, Stack, useMediaQuery } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';

import { Image } from '#features/ui/components/Image';
import { Panel } from '#features/ui/components/Panel';
import { isTypingField } from '#hooks/useTypingFieldFocused';
import { shuffledHeroes } from '../AuthPageLayout/GameMosaic';

const drift = keyframes`
  from { transform: translateY(calc(-1 * var(--marquee-drift))); }
  to { transform: translateY(var(--marquee-drift)); }
`;
const portraitsPerColumn = 10;
const desktopColumns = 7;
const compactColumns = 5;

/** Emerald UI's MIT ThreeDMarquee idea, using our bundled atlas and CSS motion. */
export const AuthCharacterMarquee = ({ compact = false }: { compact?: boolean }) => {
  const [reducedMotion] = useMediaQuery(['(prefers-reduced-motion: reduce)'], {
    ssr: true,
    fallback: [true],
  });
  const scene = useRef<HTMLDivElement>(null);
  const [heroes, setHeroes] = useState<number[]>([]);
  const [atlas, setAtlas] = useState(
    import.meta.env.AUTH_MOSAIC_ATLAS_URL || '/images/auth/hero-atlas-v1.webp'
  );
  const [loaded, setLoaded] = useState(false);
  const [visible, setVisible] = useState(true);
  const [inView, setInView] = useState(true);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setHeroes(shuffledHeroes().slice(0, desktopColumns * portraitsPerColumn));
    const visibility = () => setVisible(document.visibilityState === 'visible');
    const focus = (event?: FocusEvent) => {
      // focusout fires before activeElement changes. Buttons (including the language
      // trigger restored after closing its picker) should never pause the artwork.
      const element = event?.type === 'focusout' ? event.relatedTarget : document.activeElement;
      setFocused(
        element instanceof Element &&
          Boolean(element.closest('[data-auth-controls]')) &&
          isTypingField(element)
      );
    };
    visibility();
    focus();
    document.addEventListener('visibilitychange', visibility);
    document.addEventListener('focusin', focus);
    document.addEventListener('focusout', focus);
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? undefined
        : new IntersectionObserver(([entry]) => setInView(entry?.isIntersecting ?? false));
    if (scene.current) observer?.observe(scene.current);
    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', visibility);
      document.removeEventListener('focusin', focus);
      document.removeEventListener('focusout', focus);
    };
  }, []);

  const paused = reducedMotion || !visible || !inView || focused || !loaded;
  const columns = compact ? compactColumns : desktopColumns;
  return (
    <Box
      ref={scene}
      position="relative"
      overflow="hidden"
      w="100%"
      aspectRatio="1"
      aria-hidden="true"
      pointerEvents="none"
      data-auth-marquee=""
      data-motion={paused ? 'paused' : 'running'}
      css={{
        '--marquee-drift': compact ? 'var(--chakra-spacing-4)' : 'var(--chakra-spacing-8)',
      }}
    >
      <Image
        src={atlas}
        alt=""
        width={640}
        height={800}
        loading="eager"
        style={{ display: 'none' }}
        onLoad={() => setLoaded(true)}
        onError={() => setAtlas('/images/auth/hero-atlas-v1.webp')}
      />
      <Grid
        position="absolute"
        top="50%"
        left="50%"
        w="100%"
        templateColumns={`repeat(${columns}, minmax(0, 1fr))`}
        gap={2}
        opacity={loaded ? 1 : 0}
        transform={
          compact
            ? 'translate(-50%, -50%) rotateX(20deg) rotateZ(-20deg) scale(1.1)'
            : 'translate(-50%, -50%) rotateX(30deg) rotateZ(-25deg) scale(1.1)'
        }
      >
        {Array.from({ length: columns }, (_, column) => (
          <Stack
            key={column}
            gap={2}
            css={{
              animationName: `${drift}`,
              animationDuration: column % 2 === 1 ? 'ambientSlow' : 'ambient',
              animationDirection: column % 2 === 1 ? 'alternate-reverse' : 'alternate',
              animationIterationCount: 'infinite',
              animationTimingFunction: 'var(--chakra-easings-ease-in-out)',
              animationPlayState: paused ? 'paused' : 'running',
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            {heroes
              .slice(column * portraitsPerColumn, (column + 1) * portraitsPerColumn)
              .map((hero) => (
                <Panel
                  key={hero}
                  variant="object"
                  aspectRatio="1"
                  backgroundImage={`url('${atlas}')`}
                  backgroundSize="800% 1000%"
                  backgroundPosition={`${((hero % 8) / 7) * 100}% ${(Math.floor(hero / 8) / 9) * 100}%`}
                />
              ))}
          </Stack>
        ))}
      </Grid>
    </Box>
  );
};
